// 千日手の軍配者たち (src/twins_fight.ts) の仮の絵 (2026-10-29)。描いた絵が来るまでの図形。形と色はラボ (src/twins_proxy_lab.ts) と同じ。
//   合図は殴る側の体に出す: 避ける技 (赤) は体が赤く光る・弾く技は得物 (軍配・鈴) が動く。札や文字はゲームでは出さない。
//   2026-10-30 から本番の絵: 二人の立ち絵 (`public/sprites/bosses/twins/{ane,imo}_idle.png`・右向き) と横長の装置 (`device_wide.png`)。
//   装置の大きさは作者 c2「画面いっぱいはでかすぎるけど割と近い／横550pxよりは大きい／もう少し横長の敵に」で横 760px に決めた。
//   攻撃の姿勢の絵が来るまでは、立ち絵を傾けて (姉) と震わせて (妹) 合図にする。絵が読めない所 (テスト) では前の図形へ戻る。
import type { GameState } from './types'
import { ARENA, arenaBoundsFor } from './balance'
import { PLAN_A, RETURN, REVEAL, TSUME, curtainSpan, devRedSpan, tsumeCells, tsumeSpan, twinsFaltering, twinsReturnFlights, twinsRoom3, hopLift, twinGlowsRed, twinRiseShake, twinRising, twinsCollapseSpan, mapEdgeX, mapSpan, twinsBroken, twinsDeviceShown, twinsPlanA, twinsPrepare, twinsRevealLook, type DevAtk, type PartKind, type PuppetState, type PuppetPose, puppetPose, type TwinState, type TwinsFight } from './twins_fight'
import { drawDropBehind, drawDropFront } from './twins_reveal_drop'
import type { TwinId } from './twins_proxy_plan'
import { drawLampMote, lampMoteOld, LAMP_MOTE } from './lamp_mote'
import { UI_SERIF } from './ui_font'
import { hash01 } from './fx_kit'

const TWIN_H = 150
const FLOOR = ARENA.floorY

function shadow(ctx: CanvasRenderingContext2D, x: number, w: number, a: number): void {
  ctx.fillStyle = `rgba(0,0,0,${a})`
  ctx.beginPath(); ctx.ellipse(x, FLOOR + 4, w / 2, 8, 0, 0, Math.PI * 2); ctx.fill()
}

const DEVICE_SRC = '/sprites/bosses/twins/device_wide.png'
const DEVICE_ASPECT = 381 / 1200
/** 絵の中の目 (吊り下げた照明の青いレンズ) の位置。切り抜いた絵の幅・高さに対する割合 (色で測った・10-30)。 */
const DEVICE_EYES: [number, number][] = [[0.36, 0.356], [0.642, 0.356]]
/** 装置の横幅と、下端の床からの高さ。 */
export const DEVICE_W = 760
const DEVICE_LIFT = 30
const TWIN_SRC = { ane: '/sprites/bosses/twins/ane_idle.png', imo: '/sprites/bosses/twins/imo_idle.png' } as const
/** 立ち絵の画面での高さ (頭巾の上から裾まで)。 */
const TWIN_SPR_H = 175
/** 立ち絵の絵の高さ (px)。姿勢の絵は立ち絵と同じ縮尺で切り出してあるので、どれもこの割合で縮める。 */
const TWIN_SRC_H = 340
/** 技ごとの [合図の姿勢, 振った後の姿勢] (10-30)。絵がまだ無い姿勢は立ち絵で描く。 */
const TWIN_POSES: Record<string, Partial<Record<TwinId, [string, string]>>> = {
  a_strike: { ane: ['raise', 'chop'] },
  a_double: { ane: ['sweep', 'chop'] },
  a_lunge: { ane: ['coil', 'thrust'] },
  a_order: { ane: ['raise', 'raise'] },
  j_sennichite: { ane: ['sweep', 'chop'], imo: ['raise', 'strike'] },
  i_bell: { imo: ['ring', 'ring'] },
  i_strike: { imo: ['raise', 'strike'] },
  i_triple: { imo: ['hug', 'strike'] },
  i_slide: { imo: ['low', 'low'] },
}
/** 足元 (裾の下端の帯) の中心の、絵の幅に対する割合。絵ごとに測った値 (10-30)。 */
const POSE_FOOT: Record<string, number> = {
  ane_raise: 0.52, ane_chop: 0.449, ane_sweep: 0.526, ane_coil: 0.479, ane_thrust: 0.406,
  imo_ring: 0.507, imo_raise: 0.513, imo_strike: 0.474, imo_hug: 0.486, imo_low: 0.491,
  ane_down: 0.45, imo_down: 0.5,
  ane: 0.5, imo: 0.5, ane_rise1: 0.5, imo_rise1: 0.5,
}
/** 振った後の姿勢を見せておく長さ。 */
const RELEASE_MS = 300
const imgs = new Map<string, HTMLImageElement>()
function image(src: string): HTMLImageElement | null {
  if (typeof Image === 'undefined') return null
  let im = imgs.get(src)
  if (!im) { im = new Image(); im.src = src; imgs.set(src, im) }
  return im.complete && im.naturalWidth > 0 ? im : null
}
function deviceImage(): HTMLImageElement | null { return image(DEVICE_SRC) }

export function deviceRect(): { x0: number; x1: number; top: number; bottom: number } {
  const w = arenaBoundsFor('hollow-actor')
  const mid = (w.leftWall + w.rightWall) / 2
  const bottom = FLOOR - DEVICE_LIFT
  return { x0: mid - DEVICE_W / 2, x1: mid + DEVICE_W / 2, top: bottom - DEVICE_W * DEVICE_ASPECT, bottom }
}

function eyes(): [number, number][] {
  const r = deviceRect(), w = r.x1 - r.x0, h = r.bottom - r.top
  return DEVICE_EYES.map(([fx, fy]) => [r.x0 + w * fx, r.top + h * fy])
}

/** 案A の壊れた部品の仮の印 (絵の発注は作者が通してから)。装置の絵の上の部品の所を暗く落とし、壊れた瞬間は揺れと欠片。 */
const PART_REGION: Record<PartKind, [number, number, number, number][]> = {
  // [x0, y0, x1, y1] は装置の絵の割合。守りの駒＝歯の列、緞帳＝左右の幕、照明＝二つの目
  wall: [[0.27, 0.46, 0.73, 0.63]],
  curtain: [[0.02, 0.1, 0.26, 0.84], [0.74, 0.1, 0.98, 0.84]],
  light: [[0.31, 0.29, 0.41, 0.43], [0.59, 0.29, 0.69, 0.43]],
}
const PART_SHAKE_MS = 360
const PART_DEBRIS_MS = 900

function drawBrokenParts(ctx: CanvasRenderingContext2D, parts: TwinsFight['parts'], nowMs: number): void {
  const r = deviceRect(), w = r.x1 - r.x0, h = r.bottom - r.top
  for (const k of Object.keys(PART_REGION) as PartKind[]) {
    const at = parts[k].brokenAt
    if (!at) continue
    const age = nowMs - at
    const fade = Math.min(1, age / 220)
    for (const [fx0, fy0, fx1, fy1] of PART_REGION[k]) {
      const x0 = r.x0 + w * fx0, y0 = r.top + h * fy0, x1 = r.x0 + w * fx1, y1 = r.top + h * fy1
      ctx.save()
      ctx.globalAlpha = fade
      if (k === 'light') {
        // 灯りの消えた目: レンズを煤で塞ぐ
        ctx.fillStyle = 'rgba(12,10,14,0.86)'
        ctx.beginPath(); ctx.ellipse((x0 + x1) / 2, (y0 + y1) / 2, (x1 - x0) / 2, (y1 - y0) / 2, 0, 0, Math.PI * 2); ctx.fill()
      } else if (k === 'curtain') {
        // 落ちた幕: 幕の所を舞台の奥の暗さに沈め、裾を裂けた形に
        ctx.fillStyle = 'rgba(10,9,13,0.78)'
        ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y0)
        for (let i = 6; i >= 0; i--) ctx.lineTo(x0 + (x1 - x0) * i / 6, y1 - (i % 2 ? 0 : (y1 - y0) * 0.12))
        ctx.closePath(); ctx.fill()
      } else {
        // 崩れた守りの駒: 歯を一つおきに抜く
        ctx.fillStyle = 'rgba(10,9,13,0.9)'
        const n = 7
        for (let i = 0; i < n; i++) if (i % 2 === 0) ctx.fillRect(x0 + (x1 - x0) * i / n, y0, (x1 - x0) / n, y1 - y0)
      }
      ctx.restore()
      if (age < PART_DEBRIS_MS) {
        // 欠片: 部品の所から床へ落ちる暗い小片
        const t = age / PART_DEBRIS_MS
        ctx.save(); ctx.fillStyle = '#16141a'; ctx.globalAlpha = 1 - t
        for (let i = 0; i < 8; i++) {
          const bx = x0 + (x1 - x0) * hash01(at + i * 7.3), by = y0 + (y1 - y0) * hash01(at + i * 3.1)
          const sz = 5 + 7 * hash01(at + i)
          ctx.fillRect(bx + (hash01(at + i * 1.7) - 0.5) * 60 * t, by + 900 * t * t, sz, sz * 0.7)
        }
        ctx.restore()
      }
    }
  }
}

/** 壊れた瞬間の装置の揺れ (px)。 */
function partShake(parts: TwinsFight['parts'] | null, nowMs: number): number {
  if (!parts) return 0
  let m = 0
  for (const k of Object.keys(PART_REGION) as PartKind[]) {
    const age = nowMs - parts[k].brokenAt
    if (parts[k].brokenAt && age < PART_SHAKE_MS) m = Math.max(m, 1 - age / PART_SHAKE_MS)
  }
  return m ? Math.sin(nowMs / 18) * 9 * m : 0
}

/** 打ち返す版: 駒が当たった瞬間の揺れと、よろめきの間の傾き (px)。 */
function returnShake(f: TwinsFight | null, nowMs: number): number {
  if (!f?.lastReturn) return 0
  const age = nowMs - f.lastReturn.at
  const hit = age >= 0 && age < PART_SHAKE_MS ? Math.sin(nowMs / 18) * 12 * (1 - age / PART_SHAKE_MS) : 0
  const sway = nowMs < f.falterUntil ? Math.sin(nowMs / 140) * 5 : 0
  return hit + sway
}

function drawDevice(ctx: CanvasRenderingContext2D, active: boolean, d: DevAtk | null, nowMs: number, parts: TwinsFight['parts'] | null = null, f: TwinsFight | null = null): void {
  const r = deviceRect(), w = r.x1 - r.x0, h = r.bottom - r.top, s = w / 560
  ctx.save()
  ctx.translate(partShake(parts, nowMs) + returnShake(f, nowMs), 0)
  ctx.globalAlpha = active ? 0.95 : 0.5
  const img = deviceImage()
  if (img) {
    ctx.drawImage(img, r.x0, r.top, w, h)
    if (parts) drawBrokenParts(ctx, parts, nowMs)
    ctx.restore()
    return
  }
  ctx.fillStyle = '#25242b'; ctx.fillRect(r.x0, r.top + 30, 60 * s, h - 30); ctx.fillRect(r.x1 - 60 * s, r.top + 30, 60 * s, h - 30)
  ctx.fillStyle = '#3a3830'; ctx.fillRect(r.x0 + 80 * s, r.top + 50, w - 160 * s, h - 60)
  ctx.strokeStyle = '#5a564a'; ctx.lineWidth = 1
  for (let i = 1; i < 9; i++) {
    const x = r.x0 + 80 * s + (w - 160 * s) * i / 9, y = r.top + 50 + (h - 60) * i / 9
    ctx.beginPath(); ctx.moveTo(x, r.top + 50); ctx.lineTo(x, r.bottom - 10); ctx.stroke()
    ctx.beginPath(); ctx.moveTo(r.x0 + 80 * s, y); ctx.lineTo(r.x1 - 80 * s, y); ctx.stroke()
  }
  const mid = (r.x0 + r.x1) / 2
  ctx.fillStyle = '#2c2b30'; ctx.fillRect(mid - 70 * s, r.top + 60, 140 * s, 60)
  const lit = !!(d && (d.kind === 'light' || nowMs - d.start < 500))
  for (const [x, y] of eyes()) {
    ctx.fillStyle = lit && active ? '#fff6d8' : '#8a8578'
    ctx.beginPath(); ctx.arc(x, y, 9 * Math.min(1.4, s), 0, Math.PI * 2); ctx.fill()
  }
  ctx.restore()
}

function drawDevAtk(ctx: CanvasRenderingContext2D, d: DevAtk, nowMs: number): void {
  const b = arenaBoundsFor('hollow-actor')
  const w = { left: b.leftWall + 40, right: b.rightWall - 40, mid: (b.leftWall + b.rightWall) / 2 }
  const el = nowMs - d.start
  // 弾けない床の技は、叩く床を赤く光らせて予告する (赤＝弾けない・ガードできない、の決まりにそろえる・11-03)。
  const red = devRedSpan(d, w, nowMs)
  if (red) {
    const k = Math.min(1, el / 400)
    ctx.save(); ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = 22
    ctx.fillStyle = `rgba(255,42,42,${0.18 + 0.32 * k})`; ctx.fillRect(red[0], FLOOR - 6, red[1] - red[0], 10)
    ctx.restore()
  }
  if (d.kind === 'tsume') drawTsume(ctx, d, w, el)
  if (d.kind === 'march') {
    const edge = d.side > 0 ? w.right + 40 : w.left - 40
    if (el < 900) for (let i = 0; i < 3; i++) shadow(ctx, edge - d.side * (i * 70 + 30), 70, 0.15 + 0.5 * el / 900)
    // 駒そのものは当たり (travel) の位置に描く＝drawTwinsFight の march 駒の描画で。ここでは予告だけ。
  }
  if (d.kind === 'wall') {
    const fall = Math.min(1, Math.max(0, (el - 1000) / 120))
    shadow(ctx, d.x, el < 1120 ? 60 + 50 * Math.min(1, el / 1000) : 110, el < 1120 ? 0.2 + 0.5 * Math.min(1, el / 1000) : 0.6)
    if (d.wallHp > 0 && nowMs < d.wallUntil) {
      const y = el < 1000 ? -60 : -60 + (FLOOR + 60) * fall
      ctx.fillStyle = '#121216'; ctx.fillRect(d.x - 38, y - 230, 76, 230)
      ctx.strokeStyle = '#555'; ctx.strokeRect(d.x - 38, y - 230, 76, 230)
      ctx.fillStyle = '#cfc8b2'; ctx.font = `bold 30px ${UI_SERIF}`; ctx.textAlign = 'center'; ctx.fillText('金', d.x, y - 120)
      if (el >= 1120) { ctx.fillStyle = `rgba(255,238,150,${0.25 + 0.15 * d.wallHp})`; ctx.fillRect(d.x - 40, FLOOR - 232, 80, 4) }
    }
  }
  if (d.kind === 'map') {
    const [x0, x1] = mapSpan(d, w)
    const k = Math.min(1, el / 1300)
    ctx.fillStyle = `rgba(0,0,0,${0.15 + 0.55 * k})`; ctx.fillRect(x0, FLOOR - 4, x1 - x0, 30)
    const ang = el < 1300 ? (1 - Math.pow(k, 3)) * Math.PI / 2 : 0
    const h = el < 3500 ? 200 * Math.sin(ang) + 20 : 0
    if (el < 3500) {
      ctx.fillStyle = '#5b5040'; ctx.fillRect(x0 + 10, FLOOR - h, x1 - x0 - 20, Math.max(14, h))
      ctx.strokeStyle = '#a99c78'; ctx.strokeRect(x0 + 10, FLOOR - h, x1 - x0 - 20, Math.max(14, h))
      if (el >= 1300) { const e = mapEdgeX(d, w); ctx.fillStyle = '#fe9'; ctx.fillRect(e - 6, FLOOR - 26, 12, 26) }
    }
  }
  if (d.kind === 'curtain') {
    const [x0, x1] = curtainSpan(d, w)
    const top = FLOOR - 520
    let bottom: number
    if (el < 1400) bottom = top + 40 + 60 * el / 1400
    else if (el < 1600) bottom = top + 100 + (FLOOR - top - 100) * (el - 1400) / 200
    else bottom = FLOOR - (FLOOR - top + 40) * Math.min(1, (el - 1700) / 900)
    const k = Math.min(1, el / 1400)
    ctx.fillStyle = `rgba(0,0,0,${el < 1600 ? 0.15 + 0.5 * k : 0.6})`; ctx.fillRect(x0, FLOOR - 4, x1 - x0, 26)
    if (bottom > top) {
      for (let x = x0; x < x1; x += 30) { ctx.fillStyle = ((x - x0) / 30) % 2 ? '#c9c6c0' : '#151518'; ctx.fillRect(x, top, Math.min(30, x1 - x), bottom - top) }
      ctx.fillStyle = '#0d0d0f'; ctx.fillRect(x0 - 6, top, x1 - x0 + 12, 10)
    }
  }
  if (d.kind === 'light') {
    const fadeIn = Math.min(1, el / 250), stopped = el >= 1600
    const a = fadeIn * (el > 2600 ? Math.max(0, 1 - (el - 2600) / 500) : 1)
    const rx = 66, cx = d.lightX, cy = FLOOR + 2
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (const [ex, ey] of eyes()) {
      const warm = stopped ? '255,236,190' : '210,220,255'
      const g = ctx.createLinearGradient(ex, ey, cx, cy)
      g.addColorStop(0, `rgba(${warm},${0.42 * a})`); g.addColorStop(1, `rgba(${warm},${0.2 * a})`)
      ctx.fillStyle = g
      ctx.beginPath(); ctx.moveTo(ex - 4, ey); ctx.lineTo(ex + 4, ey); ctx.lineTo(cx + rx, cy); ctx.lineTo(cx - rx, cy); ctx.closePath(); ctx.fill()
    }
    ctx.fillStyle = `rgba(255,245,215,${0.6 * a})`
    ctx.beginPath(); ctx.ellipse(cx, cy, rx, 13, 0, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
    if (el >= 2050 && el < 3000) {
      const y = Math.min(FLOOR, FLOOR - 520 + 520 * Math.min(1, (el - 2000) / 100))
      ctx.fillStyle = '#d8d2c0'; ctx.fillRect(cx - 28, y - 86, 56, 86)
      ctx.fillStyle = '#222'; ctx.font = `bold 24px ${UI_SERIF}`; ctx.textAlign = 'center'; ctx.fillText('歩', cx, y - 36)
    }
  }
}

/** 立ち絵と同じ縮尺で、足元を t.x に置いて描く。a = 濃さ、sy = 縦の伸び (足元を基準に)、dx = 横の揺れ。 */
function drawAtFoot(ctx: CanvasRenderingContext2D, t: TwinState, spr: HTMLImageElement, key: string, a = 1, sy = 1, dx = 0): void {
  if (a <= 0.01) return
  const k = TWIN_SPR_H / TWIN_SRC_H, w = spr.naturalWidth * k, h = spr.naturalHeight * k
  ctx.save(); ctx.globalAlpha *= a; ctx.translate(t.x + dx, FLOOR + 4); ctx.scale(t.face < 0 ? -1 : 1, sy)
  ctx.drawImage(spr, -w * (POSE_FOOT[key] ?? 0.5), -h, w, h); ctx.restore()
}

// 案A の起き上がり (11-04 作者の答え rise=c「震えてから立つ」)。倒れの終わり PLAN_A.shakeMs は倒れの絵のまま小刻みに震え、
//   PLAN_A.riseMs で 倒れ → 片膝立ち (rise1) → 立ち絵 と戻る。切り替えの頭 RISE_BLEND_MS だけ前の絵を溶かす (src/boss_rise.ts と同じ考え)。
//   rise1 = Codex の原画 (そざい/concept/twins-rise-1104/) を scripts/boss_rise_cut.py で立ち絵の縮尺へ。絵が読めない時は 倒れ → 立ち絵。
const RISE_BLEND_MS = 70
/** 片膝立ちを見せる割合 (起き上がりの長さに対する)。残りで立ち絵へ伸び上がる。 */
const RISE1_SHARE = 0.55
/** 倒れの終わりの震えの横の揺れ (px)。 */
const RISE_SHAKE_PX = 3
function drawRising(ctx: CanvasRenderingContext2D, t: TwinState, nowMs: number): boolean {
  const id = t.id === 'ane' ? 'ane' : 'imo'
  const idle = image(TWIN_SRC[id]), fallen = image(`/sprites/bosses/twins/${id}_down.png`)
  if (!idle) return false
  const rise1 = image(`/sprites/bosses/twins/${id}_rise1.png`)
  const el = nowMs - (t.riseUntil - PLAN_A.riseMs)
  shadow(ctx, t.x, 80, 0.4)
  const split = rise1 ? PLAN_A.riseMs * RISE1_SHARE : 0
  if (rise1 && el < split) {
    const b = Math.min(1, el / RISE_BLEND_MS)
    if (fallen && b < 1) drawAtFoot(ctx, t, fallen, `${id}_down`, 1 - b)
    drawAtFoot(ctx, t, rise1, `${id}_rise1`, b)
    return true
  }
  // 立ち絵へ伸び上がる: 重い出だしから伸び切る (縦 0.9 → 1)。頭だけ前の絵から溶かす。
  const u = Math.min(1, (el - split) / Math.max(1, PLAN_A.riseMs - split))
  const b = Math.min(1, (el - split) / RISE_BLEND_MS)
  const from = rise1 ?? fallen
  if (from && b < 1) drawAtFoot(ctx, t, from, rise1 ? `${id}_rise1` : `${id}_down`, 1 - b)
  drawAtFoot(ctx, t, idle, id, b, 0.9 + 0.1 * (1 - (1 - u) * (1 - u)))
  return true
}

function drawTwin(ctx: CanvasRenderingContext2D, t: TwinState, nowMs: number, broken: boolean): void {
  const pale = t.id === 'ane'
  const body = pale ? '#c9c9cf' : '#1b1b20', line = pale ? '#88888f' : '#8d8d99'
  if (twinRising(t, nowMs) && drawRising(ctx, t, nowMs)) return
  if (t.down) {
    const id = t.id === 'ane' ? 'ane' : 'imo'
    const fallen = image(`/sprites/bosses/twins/${id}_down.png`)
    if (fallen) {
      // 倒れた姿 (姉＝膝をついて前へ折れる・妹＝横座りで崩れる。得物は床に落ちている)。立ち絵と同じ縮尺。
      // 案A: 起き上がる前は小刻みに震える (起きる合図)。揺れは決まった乱数で、終わりに近いほど強い。
      const sh = twinRiseShake(t, nowMs)
      const dx = sh > 0 ? (hash01(Math.floor(nowMs / 34) + (pale ? 0 : 50)) - 0.5) * 2 * RISE_SHAKE_PX * sh : 0
      shadow(ctx, t.x, 90, 0.4)
      drawAtFoot(ctx, t, fallen, `${id}_down`, 1, 1, dx)
      return
    }
    const spr = image(TWIN_SRC[id])
    if (spr) {
      // 倒れた姿の絵が来るまでは、立ち絵を床へ寝かせる (頭が向いていた側へ)。
      const h = TWIN_SPR_H, w = h * spr.naturalWidth / spr.naturalHeight
      ctx.save(); ctx.translate(t.x, FLOOR - w / 2 + 6); ctx.rotate(t.face * Math.PI / 2); ctx.globalAlpha = 0.85
      ctx.drawImage(spr, -w / 2, -h / 2, w, h); ctx.restore()
      return
    }
    ctx.fillStyle = body; ctx.fillRect(t.x - 70, FLOOR - 34, 140, 34); ctx.strokeStyle = line; ctx.strokeRect(t.x - 70, FLOOR - 34, 140, 34)
    return
  }
  const m = t.move, el = m ? nowMs - t.start : -1
  const next = m ? m.hits.map((x, i) => ({ x, i })).find(({ x, i }) => x.by === t.id && !t.done.has(i)) : undefined
  const winding = !!(next && el >= 0 && el < next.x.at)
  // 赤く光るのは、赤い当たりの前の決まった間だけ (合わせ技では相方の弾く当たりの後から・src/twins_proxy_plan.ts redGlowFrom・11-03)。
  const red = twinGlowsRed(t, nowMs)
  const crouch = (m?.slide && winding ? 40 : 0) + (broken ? 30 : 0)
  // 跳び越えの間は体だけ持ち上げる (影は床に残し、高いほど小さく薄く)。
  const lift = hopLift(t, nowMs)
  shadow(ctx, t.x, 70 * (1 - lift / 400), 0.4 * (1 - lift / 300))
  const idle = image(TWIN_SRC[t.id === 'ane' ? 'ane' : 'imo'])
  if (idle) {
    const pose = poseName(t, el)
    const spr = (pose && image(`/sprites/bosses/twins/${t.id}_${pose}.png`)) || idle
    ctx.save(); ctx.translate(0, -lift)
    drawTwinSprite(ctx, t, spr, spr === idle ? null : `${t.id}_${pose}`, { el, winding, red, crouch, at: next?.x.at ?? 1 }, nowMs)
    ctx.restore()
    return
  }
  ctx.save()
  if (red) { ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = 26 }
  ctx.fillStyle = body; ctx.fillRect(t.x - 22, FLOOR - TWIN_H + crouch, 44, TWIN_H - crouch)
  ctx.restore()
  ctx.strokeStyle = line; ctx.strokeRect(t.x - 22, FLOOR - TWIN_H + crouch, 44, TWIN_H - crouch)
  // 黒子の頭巾
  ctx.fillStyle = '#0d0d10'; ctx.fillRect(t.x - 18, FLOOR - TWIN_H - 30 + crouch, 36, 34)
  // 得物: 姉＝軍配 (合図で上げる)、妹＝鈴 (合図で揺れる)
  const handX = t.x + t.face * 26
  if (pale) {
    const up = winding ? Math.min(1, el / Math.max(1, next!.x.at - 120)) : 0
    const hy = FLOOR - 90 - up * 90 + crouch
    ctx.strokeStyle = '#ddd'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(handX, FLOOR - 80 + crouch); ctx.lineTo(handX, hy); ctx.stroke(); ctx.lineWidth = 1
    ctx.fillStyle = '#e9e4d6'; ctx.beginPath(); ctx.ellipse(handX, hy - 14, 14, 18, 0, 0, Math.PI * 2); ctx.fill()
  } else {
    const shake = winding ? Math.sin(nowMs / 30) * 6 : 0
    ctx.fillStyle = '#c8b46a'; ctx.beginPath(); ctx.arc(handX + shake, FLOOR - 82 + crouch, 9, 0, Math.PI * 2); ctx.fill()
  }
  // 残りの体力 (1人ずつ)。
  const k = Math.max(0, 1 - t.lost / Math.max(1, t.pool))
  ctx.fillStyle = '#000a'; ctx.fillRect(t.x - 30, FLOOR - TWIN_H - 44 + crouch, 60, 5)
  ctx.fillStyle = pale ? '#e0e0e8' : '#9a9aff'; ctx.fillRect(t.x - 30, FLOOR - TWIN_H - 44 + crouch, 60 * k, 5)
}

/** 技の途中で見せる姿勢の名前。合図の間は合図の姿勢、自分の一打の直後は振った後の姿勢。それ以外は null (立ち絵)。 */
export function poseName(t: TwinState, el: number): string | null {
  const m = t.move
  const p = m && TWIN_POSES[m.id]?.[t.id]
  if (!m || !p || el < 0) return null
  const mine = m.hits.filter((x) => x.by === t.id)
  if (mine.some((x) => el >= x.at && el < x.at + RELEASE_MS)) return p[1]
  return mine.some((x) => el < x.at) ? p[0] : null
}

/** 立ち絵で描く。絵は右向きなので、左を向く時は裏返す。pose が null なら立ち絵 (姿勢の絵がまだ無い時も)。 */
function drawTwinSprite(ctx: CanvasRenderingContext2D, t: TwinState, spr: HTMLImageElement, pose: string | null, a: { el: number; winding: boolean; red: boolean; crouch: number; at: number }, nowMs: number): void {
  const k = TWIN_SPR_H / TWIN_SRC_H
  const w = spr.naturalWidth * k, h = spr.naturalHeight * k * (TWIN_SPR_H - a.crouch) / TWIN_SPR_H
  const foot = pose ? POSE_FOOT[pose] ?? 0.5 : 0.5
  // 姿勢の絵が無い時の合図: 姉は体を後ろへ反らす・妹は小刻みに震える。
  const up = !pose && t.id === 'ane' && a.winding ? Math.min(1, a.el / Math.max(1, a.at - 120)) : 0
  const shake = !pose && t.id !== 'ane' && a.winding ? Math.sin(nowMs / 30) * 3 : 0
  ctx.save()
  ctx.translate(t.x + shake, FLOOR + 4)
  ctx.scale(t.face < 0 ? -1 : 1, 1)
  ctx.rotate(-0.12 * up)
  if (a.red) { ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = 26 }
  ctx.drawImage(spr, -w * foot, -h, w, h)
  ctx.restore()
  const life = Math.max(0, 1 - t.lost / Math.max(1, t.pool))
  const y = FLOOR - TWIN_SPR_H + a.crouch - 14
  ctx.fillStyle = '#000a'; ctx.fillRect(t.x - 30, y, 60, 5)
  ctx.fillStyle = t.id === 'ane' ? '#e0e0e8' : '#9a9aff'; ctx.fillRect(t.x - 30, y, 60 * life, 5)
}

/** 詰めろ: 床の盤の目。予告の間は外のマスが赤く、残る1マスが金。落ちた後は外のマスに駒が並ぶ (残る間は当たる)。 */
function drawTsume(ctx: CanvasRenderingContext2D, d: DevAtk, w: { left: number; right: number }, el: number): void {
  const { x0, cell } = tsumeCells(w)
  const [sa, sb] = tsumeSpan(d, w)
  const tell = el < TSUME.tellMs
  const k = Math.min(1, el / 500)
  const hold = !tell && el < TSUME.tellMs + TSUME.holdMs
  const fade = hold ? 1 : tell ? 1 : Math.max(0, 1 - (el - TSUME.tellMs - TSUME.holdMs) / 300)
  ctx.save()
  for (let c = 0; c < TSUME.cells; c++) {
    const a = x0 + cell * c
    const safe = a >= sa - 1 && a < sb - 1
    if (safe) {
      ctx.shadowColor = '#ffd77a'; ctx.shadowBlur = 18
      ctx.fillStyle = `rgba(255,215,122,${(0.14 + 0.22 * k) * fade})`; ctx.fillRect(a + 3, FLOOR - 6, cell - 6, 10)
      ctx.strokeStyle = `rgba(255,224,150,${0.7 * k * fade})`; ctx.lineWidth = 2; ctx.strokeRect(a + 3, FLOOR - 7, cell - 6, 12)
      ctx.shadowBlur = 0
      continue
    }
    // 外のマス: 予告は赤い床、落ちた後は駒 (暗い札) が立つ。
    ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = tell ? 22 : 10
    const pulse = tell ? 0.18 + 0.3 * k + 0.08 * Math.sin(el / 90) : 0.35
    ctx.fillStyle = `rgba(255,42,42,${pulse * fade})`; ctx.fillRect(a + 3, FLOOR - 6, cell - 6, 10)
    ctx.shadowBlur = 0
    if (tell) {
      const fall = Math.max(0, (el - (TSUME.tellMs - 500)) / 500)
      shadow(ctx, a + cell / 2, 50 + 40 * Math.min(1, el / TSUME.tellMs), 0.15 + 0.45 * Math.min(1, el / TSUME.tellMs))
      if (fall > 0) { ctx.fillStyle = '#121216'; ctx.fillRect(a + cell / 2 - 26, -80 + (FLOOR - 120 + 80) * fall * fall, 52, 120) }
    } else if (fade > 0) {
      ctx.globalAlpha = fade
      ctx.fillStyle = '#121216'; ctx.fillRect(a + cell / 2 - 26, FLOOR - 120, 52, 120)
      ctx.strokeStyle = '#6a2a2a'; ctx.strokeRect(a + cell / 2 - 26, FLOOR - 120, 52, 120)
      ctx.globalAlpha = 1
    }
  }
  ctx.restore()
}

/** 打ち返す版: 白く光る駒 (弾くと装置へ戻る)・装置へ戻る駒・よろめきの間の斬れる所。 */
function drawReturnPieces(ctx: CanvasRenderingContext2D, state: GameState, f: TwinsFight, nowMs: number): void {
  ctx.save()
  for (const h of state.hits) {
    if (h.attackId !== 'twins_dev_return' || h.x === undefined || h.consumed) continue
    const x = h.x, y = h.y ?? FLOOR - 60
    ctx.globalCompositeOperation = 'lighter'
    glow(ctx, x, y, 34, 0.55)
    ctx.globalCompositeOperation = 'source-over'
    ctx.fillStyle = '#f4f1ea'; ctx.beginPath()
    ctx.moveTo(x - 16, y + 22); ctx.lineTo(x + 16, y + 22); ctx.lineTo(x + 12, y - 16); ctx.lineTo(x, y - 26); ctx.lineTo(x - 12, y - 16); ctx.closePath(); ctx.fill()
  }
  const r = deviceRect(), tx = (r.x0 + r.x1) / 2, ty = r.top + (r.bottom - r.top) * 0.5
  for (const fl of twinsReturnFlights(state.time)) {
    // 弾いた所から装置の胸へ、弧を描いて戻る (尾を引く)。
    for (let i = 0; i < 6; i++) {
      const u = Math.max(0, fl.u - i * 0.05)
      const x = fl.x + (tx - fl.x) * u, y = FLOOR - 60 + (ty - FLOOR + 60) * u - Math.sin(Math.PI * u) * 140
      ctx.globalCompositeOperation = 'lighter'
      glow(ctx, x, y, 26 - i * 3, 0.7 - i * 0.1)
    }
  }
  ctx.globalCompositeOperation = 'source-over'
  if (f.lastReturn) {
    const age = nowMs - f.lastReturn.at
    if (age >= 0 && age < 420) { ctx.globalCompositeOperation = 'lighter'; glow(ctx, tx, ty, 120 + age * 0.3, 0.8 * (1 - age / 420)); ctx.globalCompositeOperation = 'source-over' }
  }
  if (twinsFaltering(state.time)) {
    // よろめき: 装置の手 (仮の暗い柱) が床へ落ち、金の縁が「斬れる」を示す。
    const x = f.falterX, left = f.falterUntil - nowMs
    const drop = Math.min(1, (RETURN_FALTER_MS - left) / 180)
    const top = FLOOR - 150 * drop
    ctx.fillStyle = '#1a1820'; ctx.fillRect(x - 30, -40 + (top + 40) * drop - (1 - drop) * 40, 60, FLOOR - top)
    ctx.fillStyle = `rgba(255,224,150,${0.45 + 0.25 * Math.sin(nowMs / 80)})`; ctx.fillRect(x - 32, top - 4, 64, 4)
  }
  ctx.restore()
}
const RETURN_FALTER_MS = RETURN.falterMs

/** 吊られた二人の姿勢の絵 (11-05・型の描き方で姿勢ごと)。絵は右向き・立ち絵と同じ縮尺 (TWIN_SRC_H の割合で縮める)。
 *  foot＝体を置く横位置 (絵の幅の割合・下端)。pins＝糸を結ぶ所 [背中の帯・手前の手首・奥の手首] (幅・高さの割合)。
 *  頭・首・顎の下には結ばない (11-05 作者「首吊りみたいに見えないようにだけ注意してほしい」)。操り人形の留め方＝帯の背と両手首。 */
const PUPPET_ART: Record<TwinId, Record<PuppetPose, { src: string; foot: number; pins: [number, number][] }>> = {
  ane: {
    hang: { src: '/sprites/bosses/twins/ane_hang.png', foot: 0.45, pins: [[0.38, 0.26], [0.86, 0.17], [0.3, 0.12]] },
    hop: { src: '/sprites/bosses/twins/ane_hang.png', foot: 0.45, pins: [[0.38, 0.26], [0.86, 0.17], [0.3, 0.12]] },
    swing: { src: '/sprites/bosses/twins/ane_swing.png', foot: 0.45, pins: [[0.52, 0.3], [0.79, 0.42], [0.28, 0.29]] },
    drag: { src: '/sprites/bosses/twins/ane_hang.png', foot: 0.45, pins: [[0.38, 0.26], [0.86, 0.17], [0.3, 0.12]] },
    thrust: { src: '/sprites/bosses/twins/ane_thrust_p.png', foot: 0.32, pins: [[0.38, 0.25], [0.9, 0.23], [0.48, 0.55]] },
  },
  imo: {
    hang: { src: '/sprites/bosses/twins/imo_hang.png', foot: 0.39, pins: [[0.42, 0.26], [0.95, 0.18], [0.33, 0.18]] },
    hop: { src: '/sprites/bosses/twins/imo_hop.png', foot: 0.5, pins: [[0.62, 0.37], [0.92, 0.05], [0.35, 0.02]] },
    swing: { src: '/sprites/bosses/twins/imo_hang.png', foot: 0.39, pins: [[0.42, 0.26], [0.95, 0.18], [0.33, 0.18]] },
    drag: { src: '/sprites/bosses/twins/imo_drag.png', foot: 0.5, pins: [[0.6, 0.39], [0.97, 0.21], [0.92, 0.31]] },
    thrust: { src: '/sprites/bosses/twins/imo_thrust_p.png', foot: 0.35, pins: [[0.44, 0.28], [0.94, 0.19], [0.55, 0.55]] },
  },
}
/** 常夜燈で吊られた二人 (案 b)。姿勢ごとの絵を元の明るさのまま描き、上の梁から青い糸で吊る (作者 11-05「元の明るさのままでいい」)。
 *  赤く光る間は体を赤く染める。 */
function drawPuppets(ctx: CanvasRenderingContext2D, state: GameState, f: TwinsFight, nowMs: number): void {
  for (const p of f.puppets) drawPuppet(ctx, state, p, nowMs)
}
function drawPuppet(ctx: CanvasRenderingContext2D, state: GameState, p: PuppetState, nowMs: number): void {
  const { pose, face: poseFace } = puppetPose(p, nowMs)
  const art = PUPPET_ART[p.id][pose]
  const spr = image(art.src)
  const sway = pose === 'hang' ? Math.sin(nowMs / 520 + (p.id === 'ane' ? 0 : 2)) * (p.lift > 40 ? 4 : 1.5) : 0
  const x = p.x + sway, foot = FLOOR + 4 - p.lift
  const k = TWIN_SPR_H / TWIN_SRC_H
  const w = spr ? spr.naturalWidth * k : 70, h = spr ? spr.naturalHeight * k : TWIN_SPR_H
  const face = poseFace ?? (state.player.x >= p.x ? 1 : -1)
  const red = nowMs >= p.redFrom && nowMs < p.redUntil
  const left = -art.foot * w
  // 糸: 画面の上 (梁) から背中の帯・両手首へ。色は常夜燈 (src/lamp_mote.ts の LAMP_MOTE・11-05 作者「糸は常夜燈の色に」)。
  //   振り子は後ろの上から、引きずりは前の上から引く。
  ctx.save()
  ctx.strokeStyle = rgbaOf(LAMP_MOTE.mid, 0.85); ctx.lineWidth = 1.5
  ctx.shadowColor = rgbaOf(LAMP_MOTE.deep, 1); ctx.shadowBlur = 8
  const lead = pose === 'swing' ? -60 : pose === 'drag' ? 160 : 0
  art.pins.forEach(([fx, fy], i) => {
    const px = x + face * (left + fx * w), py = foot - h + fy * h
    const tx = x + face * (lead + (i === 0 ? -10 : i === 1 ? 22 : -22)) + sway * 2
    ctx.beginPath(); ctx.moveTo(tx, -10); ctx.lineTo(px, py); ctx.stroke()
  })
  ctx.restore()
  // 影は床に。高く吊られている間は薄い。
  shadow(ctx, p.x, 70 * Math.max(0.3, 1 - p.lift / 300), 0.4 * Math.max(0.2, 1 - p.lift / 260))
  ctx.save()
  ctx.translate(x, foot)
  ctx.scale(face, 1)
  if (spr && aura && !red) drawAuraBehind(ctx, spr, left, w, h, nowMs, p.id === 'ane' ? 0 : 1.7)
  if (red) { ctx.shadowColor = '#ff2a2a'; ctx.shadowBlur = 28 }
  if (spr) {
    // 赤の前は体だけを赤く染める (枠の四角は塗らない)。それ以外は元の絵の明るさのまま。
    if (red) ctx.filter = `brightness(${0.55 + 0.15 * Math.sin(nowMs / 60)}) sepia(1) saturate(7) hue-rotate(-45deg)`
    ctx.drawImage(spr, left, -h, w, h)
    ctx.filter = 'none'
    if (aura && !red) drawAuraFront(ctx, spr, left, w, h, nowMs, p.id === 'ane' ? 0 : 1.7)
  } else {
    ctx.fillStyle = red ? '#7a1616' : '#16161c'; ctx.fillRect(left, -h, w, h)
  }
  ctx.restore()
}

/** 二人の輪郭を包む常夜燈のオーラ (11-05 作者「2人の絵の周り、輪郭を…常夜燈のオーラで包んで欲しい」)。
 *  好みを選ぶための6案。開発版 &twinsAura=1..6 だけ (既定は 0＝無し)。色はどれも常夜燈 (LAMP_MOTE)、体の明るさは変えない。
 *  1 細い縁取り / 2 外へにじむ光 / 3 揺らめく炎の縁 / 4 輪郭から昇る粒 / 5 縁を走る光 / 6 内側から透ける光 */
let aura = 0
export function setTwinsAura(v: string | null): void { const n = Number(v); aura = Number.isInteger(n) && n >= 1 && n <= 6 ? n : 0 }
const rgbaOf = (rgb: readonly [number, number, number], a: number): string => `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${a})`
const AURA_PAD = 28
type Sil = { cv: HTMLCanvasElement; edge: [number, number][] }
const silCache = new Map<string, Sil>()
/** 絵の形を常夜燈の色で塗った影絵 (余白 AURA_PAD つき・画面の大きさ)。輪郭の点も一度だけ拾う。 */
function silhouette(spr: HTMLImageElement, w: number, h: number): Sil | null {
  if (typeof document === 'undefined') return null
  const key = `${spr.src}|${Math.round(w)}x${Math.round(h)}`
  const got = silCache.get(key); if (got) return got
  const cv = document.createElement('canvas'); cv.width = Math.ceil(w + AURA_PAD * 2); cv.height = Math.ceil(h + AURA_PAD * 2)
  const g = cv.getContext('2d'); if (!g) return null
  g.drawImage(spr, AURA_PAD, AURA_PAD, w, h)
  g.globalCompositeOperation = 'source-in'; g.fillStyle = rgbaOf(LAMP_MOTE.mid, 1); g.fillRect(0, 0, cv.width, cv.height)
  const edge: [number, number][] = []
  try {
    const d = g.getImageData(0, 0, cv.width, cv.height).data, W = cv.width
    const a = (x: number, y: number) => d[(y * W + x) * 4 + 3]
    for (let y = 2; y < cv.height - 2; y += 3) for (let x = 2; x < W - 2; x += 3) {
      if (a(x, y) > 128 && (a(x - 2, y) < 64 || a(x + 2, y) < 64 || a(x, y - 2) < 64 || a(x, y + 2) < 64)) edge.push([x - AURA_PAD, y - AURA_PAD])
    }
  } catch { /* 読めない時は粒なし */ }
  const sil = { cv, edge }
  silCache.set(key, sil)
  return sil
}
let tmp: HTMLCanvasElement | null = null
function scratch(w: number, h: number): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null
  if (!tmp) tmp = document.createElement('canvas')
  if (tmp.width < w) tmp.width = w
  if (tmp.height < h) tmp.height = h
  const g = tmp.getContext('2d'); if (!g) return null
  g.setTransform(1, 0, 0, 1, 0, 0); g.globalCompositeOperation = 'source-over'; g.globalAlpha = 1; g.filter = 'none'
  g.clearRect(0, 0, w, h)
  return g
}
/** 体の後ろに描く分 (縁取り・にじみ・炎・走る光の下地)。ctx は足元が原点・向きで反転済み。 */
function drawAuraBehind(ctx: CanvasRenderingContext2D, spr: HTMLImageElement, left: number, w: number, h: number, nowMs: number, ph: number): void {
  const sil = silhouette(spr, w, h); if (!sil) return
  const ox = left - AURA_PAD, oy = -h - AURA_PAD, t = nowMs / 1000 + ph
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (aura === 1) {
    // 細い縁取り: 影絵を八方へ 2px ずらして重ね、体で隠す。くっきりした一本の光の線。
    ctx.globalAlpha = 0.55
    for (let i = 0; i < 8; i++) { const r = i / 8 * Math.PI * 2; ctx.drawImage(sil.cv, ox + Math.cos(r) * 2, oy + Math.sin(r) * 2) }
  } else if (aura === 2) {
    // 外へにじむ柔らかい光: ぼかした影絵を2段 (広く薄く・狭く濃く)。ゆっくり息をする。
    const br = 0.85 + 0.15 * Math.sin(t * 1.6)
    ctx.filter = 'blur(14px)'; ctx.globalAlpha = 0.75 * br; ctx.drawImage(sil.cv, ox, oy)
    ctx.filter = 'blur(5px)'; ctx.globalAlpha = 0.6 * br; ctx.drawImage(sil.cv, ox, oy)
  } else if (aura === 3) {
    // 揺らめく炎の縁: 影絵を上へずらした写しを何枚か、横に揺らして重ねる。上へ行くほど薄く細い。
    //   舌の数だけ写しを上へ伸ばし (高さを伸ばして先を細く)、写しごとに違う速さで横へ揺らす。にじみは弱く、形の揺れで見せる。
    ctx.filter = 'blur(1.5px)'
    for (let i = 1; i <= 7; i++) {
      const dx = Math.sin(t * (6 + i) + i * 1.9) * (2 + i * 1.1), up = i * 4.5 + (Math.sin(t * 11 + i * 2.3) + 1) * 3
      ctx.globalAlpha = 0.5 * (1 - i / 8.5)
      ctx.drawImage(sil.cv, ox + dx, oy - up, sil.cv.width, sil.cv.height + up * 0.4)
    }
  } else if (aura === 4) {
    // 輪郭から昇る粒の下地: ごく薄いにじみ。
    ctx.filter = 'blur(6px)'; ctx.globalAlpha = 0.28; ctx.drawImage(sil.cv, ox, oy)
  } else if (aura === 5) {
    // 縁を走る光: 薄い縁取りの上を、明るい帯が足元から頭へ、糸を伝うように昇っていく。
    const g = scratch(sil.cv.width, sil.cv.height); if (g) {
      for (let i = 0; i < 8; i++) { const r = i / 8 * Math.PI * 2; g.drawImage(sil.cv, Math.cos(r) * 2, Math.sin(r) * 2) }
      g.globalCompositeOperation = 'source-in'
      const H = sil.cv.height, k = 1 - ((t * 0.8) % 1), y0 = k * (H + 160) - 80
      const lg = g.createLinearGradient(0, y0 - 80, 0, y0 + 80)
      lg.addColorStop(0, rgbaOf(LAMP_MOTE.deep, 0.25)); lg.addColorStop(0.5, rgbaOf(LAMP_MOTE.core, 1)); lg.addColorStop(1, rgbaOf(LAMP_MOTE.deep, 0.25))
      g.fillStyle = rgbaOf(LAMP_MOTE.deep, 0.5); g.fillRect(0, 0, sil.cv.width, H)
      g.fillStyle = lg; g.fillRect(0, y0 - 80, sil.cv.width, 160)
      ctx.globalAlpha = 0.9; ctx.drawImage(tmp!, 0, 0, sil.cv.width, H, ox, oy, sil.cv.width, H)
      ctx.filter = 'blur(4px)'; ctx.globalAlpha = 0.5; ctx.drawImage(tmp!, 0, 0, sil.cv.width, H, ox, oy, sil.cv.width, H)
    }
  }
  ctx.restore()
}
/** 体の前に描く分 (昇る粒・内側から透ける光)。 */
function drawAuraFront(ctx: CanvasRenderingContext2D, spr: HTMLImageElement, left: number, w: number, h: number, nowMs: number, ph: number): void {
  const sil = silhouette(spr, w, h); if (!sil) return
  const t = nowMs / 1000 + ph
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  if (aura === 4 && sil.edge.length) {
    // 輪郭の点から常夜燈の粒 (1粒の正本 drawLampMote) が昇って消える。同じ時刻なら同じ並び (乱数なし)。
    const N = 26, n = sil.edge.length
    for (let i = 0; i < N; i++) {
      const life = 1.6 + (i % 5) * 0.25, u = ((t + i * 0.37) / life) % 1, cyc = Math.floor((t + i * 0.37) / life)
      const [ex, ey] = sil.edge[((i * 7919 + cyc * 104729) % n + n) % n]
      drawLampMote(ctx, left + ex + Math.sin(t * 2 + i) * 3, -h + ey - u * 34, Math.sin(u * Math.PI) * 0.85)
    }
  } else if (aura === 6) {
    // 内側から透ける光: 影絵からぼかした影絵をくり抜き、体の縁の内側だけ光らせる。
    const g = scratch(sil.cv.width, sil.cv.height); if (g) {
      g.drawImage(sil.cv, 0, 0)
      g.globalCompositeOperation = 'destination-out'; g.filter = 'blur(7px)'; g.drawImage(sil.cv, 0, 0)
      g.filter = 'none'; g.globalCompositeOperation = 'destination-in'; g.drawImage(sil.cv, 0, 0)
      ctx.globalAlpha = 0.45 + 0.12 * Math.sin(t * 1.6)
      ctx.drawImage(tmp!, 0, 0, sil.cv.width, sil.cv.height, left - AURA_PAD, -h - AURA_PAD, sil.cv.width, sil.cv.height)
      ctx.filter = 'blur(3px)'; ctx.globalAlpha = 0.3
      ctx.drawImage(tmp!, 0, 0, sil.cv.width, sil.cv.height, left - AURA_PAD, -h - AURA_PAD, sil.cv.width, sil.cv.height)
    }
  }
  ctx.restore()
}

/** 吊られた二人の当たり (振り子＝白・引きずり＝赤)。体と重なるので、体の前に薄い光だけ足す。 */
function drawPuppetHits(ctx: CanvasRenderingContext2D, state: GameState): void {
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  for (const h of state.hits) {
    if ((h.attackId !== 'twins_puppet_ride' && h.attackId !== 'twins_puppet_red') || h.x === undefined || h.consumed) continue
    if (h.attackId === 'twins_puppet_ride') glow(ctx, h.x, h.y ?? FLOOR - 60, 46, 0.6)
    else {
      const g = ctx.createRadialGradient(h.x, FLOOR - 60, 0, h.x, FLOOR - 60, 60)
      g.addColorStop(0, 'rgba(255,60,60,0.55)'); g.addColorStop(1, 'rgba(255,0,0,0)')
      ctx.fillStyle = g; ctx.fillRect(h.x - 60, FLOOR - 120, 120, 120)
    }
  }
  for (const h of state.hits) {
    if (h.attackId !== 'twins_puppet_red' || h.follow === 'travel' || h.consumed) continue
    ctx.fillStyle = 'rgba(255,50,50,0.35)'; ctx.fillRect(h.xStart, FLOOR - 70, h.xEnd - h.xStart, 70)
  }
  ctx.restore()
}

/** 行軍の駒 (travel の当たり) の絵。弾いて返した駒も同じ形。 */
function drawMarchPieces(ctx: CanvasRenderingContext2D, state: GameState): void {
  for (const h of state.hits) {
    if (h.attackId !== 'twins_dev_march' || h.x === undefined || (h.consumed && !h.reflected)) continue
    const x = h.x
    shadow(ctx, x, 80, 0.55)
    ctx.fillStyle = '#d8d2c0'; ctx.beginPath()
    ctx.moveTo(x - 32, FLOOR); ctx.lineTo(x + 32, FLOOR); ctx.lineTo(x + 24, FLOOR - 80); ctx.lineTo(x, FLOOR - 98); ctx.lineTo(x - 24, FLOOR - 80); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#222'; ctx.font = `bold 26px ${UI_SERIF}`; ctx.textAlign = 'center'; ctx.fillText('歩', x, FLOOR - 40)
  }
  for (const h of state.hits) {
    if (h.follow !== 'travel' || h.attackId === 'twins_dev_march' || h.attackId === 'twins_dev_return' || h.attackId.startsWith('twins_puppet') || !h.attackId.startsWith('twins_') || h.x === undefined || (h.consumed && !h.reflected)) continue
    ctx.fillStyle = h.reflected ? '#ffe9a0' : '#e6d48a'
    ctx.beginPath(); ctx.arc(h.x, h.y ?? FLOOR - 90, 9, 0, Math.PI * 2); ctx.fill()
  }
}

// ---- 3部屋目の現れ (10-30 作者「2幕のちめいをいれてから光を浮上させた後、画面中央に光が集中して突如として3幕目が現れる」) ----
//   光は常夜燈の青 (#aacdff) より白い青白。線や輪は使わず、ぼかした粒と塊だけで描く。
const REVEAL_MOTES = 16
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
/** 決まった乱数 (毎コマ同じ粒を同じ所に出す)。 */
function hash(n: number): number { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x) }
function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number): void {
  if (a <= 0.003 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(240,248,255,${a})`)
  g.addColorStop(0.35, `rgba(196,222,255,${a * 0.55})`)
  g.addColorStop(1, 'rgba(170,205,255,0)')
  ctx.fillStyle = g
  ctx.fillRect(x - r, y - r, r * 2, r * 2)
}
/** 光が集まる所 (装置の二つの目の間)。 */
export function revealCenter(): { x: number; y: number } {
  const [[x0, y0], [x1, y1]] = eyes() as [[number, number], [number, number]]
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2 }
}
/** 粒の位置と濃さ。e = 現れの始まりからの ms。見えない時は null。 */
export function revealMote(e: number, bodyX: number, k: number, c: { x: number; y: number }): { x: number; y: number; a: number; r: number } | null {
  const delay = hash(k) * 520, dx = (hash(k + 0.5) - 0.5) * 90, sy = FLOOR - 20 - hash(k + 0.25) * 60
  const riseH = 70 + hash(k + 0.75) * 110
  const t0 = REVEAL.rise + delay
  if (e < t0 || e >= REVEAL.appear) return null
  const u = clamp01((e - t0) / (REVEAL.gather - REVEAL.rise))
  const ease = 1 - (1 - u) * (1 - u)
  const rx = bodyX + dx + Math.sin(e / 260 + k * 1.7) * 10 * ease, ry = sy - riseH * ease
  const v = clamp01((e - REVEAL.gather) / (REVEAL.appear - REVEAL.gather))
  const pull = v * v * v
  return { x: rx + (c.x - rx) * pull, y: ry + (c.y - ry) * pull, a: Math.min(1, (e - t0) / 220) * (0.55 + 0.45 * pull), r: 9 + hash(k + 0.1) * 7 - pull * 4 }
}
function fallenTwin(id: TwinId, x: number, face: 1 | -1): TwinState {
  return { id, x, face, pool: 1, lost: 1, down: true, downUntil: 0, riseUntil: 0, move: null, start: 0, booked: [], done: new Set(), nextThink: 0, fromX: x, lunge: 0, crossing: false, told: false, hop: null }
}
/** 比較版 C (上から落ちる重さ・src/twins_reveal_drop.ts) の装置の後ろ。装置より先に描く。 */
function drawDropBehindOf(ctx: CanvasRenderingContext2D, f: TwinsFight, nowMs: number): void {
  const rv = f.reveal
  if (!rv || twinsRevealLook() !== 'drop') return
  drawDropBehind(ctx, nowMs - rv.start, deviceRect(), FLOOR, rv.bodies,
    (alpha) => {
      ctx.save(); ctx.globalAlpha = alpha
      for (const b of rv.bodies) drawTwin(ctx, fallenTwin(b.id, b.x, b.face), nowMs, false)
      ctx.restore()
    },
    (dy, stretch) => {
      const r = deviceRect()
      ctx.save(); ctx.translate(0, r.bottom + dy); ctx.scale(1, stretch); ctx.translate(0, -r.bottom)
      drawDevice(ctx, true, null, nowMs)
      ctx.restore()
    })
}
function drawRevealBefore(ctx: CanvasRenderingContext2D, f: TwinsFight, nowMs: number): void {
  const rv = f.reveal
  if (!rv || twinsRevealLook() !== 'light') return
  const e = nowMs - rv.start
  if (e >= REVEAL.appear) return
  // 倒れた二人: 光が浮かぶにつれて薄れて消える。
  const fade = 1 - clamp01((e - REVEAL.rise) / (REVEAL.gather - REVEAL.rise - 200))
  if (fade > 0) {
    ctx.save(); ctx.globalAlpha = fade
    for (const b of rv.bodies) {
      drawTwin(ctx, fallenTwin(b.id, b.x, b.face), nowMs, false)
    }
    ctx.restore()
  }
  const c = revealCenter()
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  rv.bodies.forEach((b, bi) => {
    // 体の上に残る薄い光 (粒が抜けていく元)。
    glow(ctx, b.x, FLOOR - 50, 80, 0.22 * clamp01((e - REVEAL.rise) / 400) * fade)
    for (let i = 0; i < REVEAL_MOTES; i++) {
      const m = revealMote(e, b.x, bi * 100 + i, c)
      // 1粒は常夜燈の光の正本 (src/lamp_mote.ts・10-31)。前の版 (白っぽいにじみ・半径 22〜38) = DEV ?lampMote=old。
      if (m) { if (lampMoteOld()) glow(ctx, m.x, m.y, m.r * 2.4, m.a * 0.8); else drawLampMote(ctx, m.x, m.y, m.a) }
    }
  })
  // 中央に集まる光の塊。集まるほど強くなる。
  const v = clamp01((e - REVEAL.gather) / (REVEAL.appear - REVEAL.gather))
  glow(ctx, c.x, c.y, 30 + 90 * v * v, 0.25 + 0.75 * v)
  ctx.restore()
}
function drawRevealAfter(ctx: CanvasRenderingContext2D, f: TwinsFight, nowMs: number): void {
  const rv = f.reveal
  if (!rv) return
  if (twinsRevealLook() === 'drop') { drawDropFront(ctx, nowMs - rv.start, deviceRect(), eyes()); return }
  const e = nowMs - rv.start
  if (e < REVEAL.appear || e >= REVEAL.end) return
  // 装置が出た一瞬、集めた光が弾けて部屋を明るくし、すぐ引く。
  const w = 1 - clamp01((e - REVEAL.appear) / (REVEAL.end - REVEAL.appear))
  const c = revealCenter()
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  glow(ctx, c.x, c.y, 160 + 520 * (1 - w), w * w)
  ctx.fillStyle = `rgba(190,215,255,${0.3 * w * w * w})`
  ctx.fillRect(c.x - 2000, c.y - 2000, 4000, 4000)
  ctx.restore()
}

// ---- 案A の総崩れ (11-04 作者の答え collapse=a「膝をついたまま光る・足元に金の光」) ----
//   倒れた二人の足元の床に、灯明のような鈍い金の光がにじみ、細かな金の粉がゆっくり昇る。「今が体幹の貯め時」の合図。
//   線・輪・縁のある形は使わず、ぼかしたにじみと粒だけ (コードで描いたと分かる線・ネオンを避ける・10-30 作者)。
//   色は灯明の金 (白に寄せない・彩度を抑える)。二人の灰と黒の着物に、下から温かい光が少し当たる程度の濃さ。
const COLLAPSE_GOLD = { core: '255,226,160', mid: '226,176,92', deep: '150,98,40' } as const
const COLLAPSE_MOTES = 7
function drawCollapseGlow(ctx: CanvasRenderingContext2D, f: TwinsFight, nowMs: number, span: { from: number; until: number }, layer: 'floor' | 'motes'): void {
  const fadeIn = clamp01((nowMs - span.from) / 260), fadeOut = clamp01((span.until - nowMs) / 450)
  const env = fadeIn * fadeOut
  if (env <= 0.01) return
  const breathe = 0.85 + 0.15 * Math.sin(nowMs / 420)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (const [i, t] of f.twins.entries()) {
    if (layer === 'floor') {
      // 床のにじみ: 横長に潰した放射のぼかし (縁は残さない)。
      const a = 0.34 * env * breathe
      ctx.save(); ctx.translate(t.x, FLOOR + 2); ctx.scale(1, 0.2)
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 120)
      g.addColorStop(0, `rgba(${COLLAPSE_GOLD.core},${a})`)
      g.addColorStop(0.4, `rgba(${COLLAPSE_GOLD.mid},${a * 0.55})`)
      g.addColorStop(1, `rgba(${COLLAPSE_GOLD.deep},0)`)
      ctx.fillStyle = g; ctx.fillRect(-120, -120, 240, 240); ctx.restore()
      // 体の下の方へ薄く照り返す (床から上へ消える)。
      const h = ctx.createRadialGradient(t.x, FLOOR, 0, t.x, FLOOR, 90)
      h.addColorStop(0, `rgba(${COLLAPSE_GOLD.mid},${0.12 * env * breathe})`)
      h.addColorStop(1, `rgba(${COLLAPSE_GOLD.deep},0)`)
      ctx.fillStyle = h; ctx.fillRect(t.x - 90, FLOOR - 90, 180, 90)
      continue
    }
    // 金の粉: 足元から 70〜130px ゆっくり昇って消える。粒ごとに決まった間で繰り返す。
    for (let k = 0; k < COLLAPSE_MOTES; k++) {
      const seed = i * 31 + k * 7.3
      const period = 1500 + hash01(seed) * 900
      const u = ((nowMs - span.from + hash01(seed + 1) * period) % period) / period
      const x = t.x + (hash01(seed + 2) - 0.5) * 120 + Math.sin(u * 5 + seed) * 6
      const y = FLOOR - 4 - u * (70 + hash01(seed + 3) * 60)
      const a = env * Math.sin(Math.PI * u) * 0.7
      const r = 5 + hash01(seed + 4) * 3
      const g = ctx.createRadialGradient(x, y, 0, x, y, r)
      g.addColorStop(0, `rgba(${COLLAPSE_GOLD.core},${a})`)
      g.addColorStop(0.3, `rgba(${COLLAPSE_GOLD.mid},${a * 0.6})`)
      g.addColorStop(1, `rgba(${COLLAPSE_GOLD.deep},0)`)
      ctx.fillStyle = g; ctx.fillRect(x - r, y - r, r * 2, r * 2)
    }
  }
  ctx.restore()
}

/** drawHollowActor の代わり。描いたら true。 */
export function drawTwinsFight(ctx: CanvasRenderingContext2D, state: GameState): boolean {
  const f = twinsPrepare(state.boss, state.player, state.time)
  const nowMs = state.time * 1000
  ctx.save()
  drawDropBehindOf(ctx, f, nowMs)
  // 装置は3部屋目に現れてから描く (1・2部屋目は画面のどこにも出さない・10-30 作者「3幕の姿は3幕になるまで出さないで」)。
  if (twinsDeviceShown(f, nowMs)) {
    drawDevice(ctx, f.devLost < f.devPool, f.dev, nowMs, twinsPlanA() ? f.parts : null, twinsPlanA() && twinsRoom3() === 'return' ? f : null)
    if (f.dev) drawDevAtk(ctx, f.dev, nowMs)
  }
  drawRevealBefore(ctx, f, nowMs)
  const broken = twinsBroken(state.time)
  const collapse = twinsCollapseSpan(state.time)
  if (collapse) drawCollapseGlow(ctx, f, nowMs, collapse, 'floor')
  // 赤の合図の方を手前に描く (重なった時に見える)。
  for (const t of [...f.twins].sort((a, b) => Number(!!a.move) - Number(!!b.move))) drawTwin(ctx, t, nowMs, broken)
  if (collapse) drawCollapseGlow(ctx, f, nowMs, collapse, 'motes')
  drawMarchPieces(ctx, state)
  if (twinsPlanA() && twinsRoom3() === 'return' && twinsDeviceShown(f, nowMs)) {
    drawPuppets(ctx, state, f, nowMs)
    drawPuppetHits(ctx, state)
    drawReturnPieces(ctx, state, f, nowMs)
  }
  drawRevealAfter(ctx, f, nowMs)
  ctx.restore()
  return true
}
